import React from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';

import { ArrowRightLarger } from '@/components/Iconography';
import getUrl from '@/routes/routes/helpers/getUrl';
import Icon, { Icons } from '@shared/Icons/Icon';
import GlobalSearch from '@shared/GlobalSearch';
import UserMenu from '@/containers/UserMenu';
import { getRelativeUrl } from '@/utils/routing';
import { useProfile } from '@/contexts/profile';
import { usePermissions } from '@/hooks/usePermissions';

import CategoriesNav from '../CategoriesNav';

import {
  srOnly,
  hideAboveHeaderStackedListBreakpoint,
  hideBelowLG,
} from '../../../../base-styles/mixins.module.css';

import {
  allSportsLink,
  arrow,
  close,
  itemCategories,
  itemOrganiser,
  itemUser,
  lineBreak,
  link,
  list,
  nav,
  search,
  searchLink,
  user,
  narrowStyle,
} from './styles.module.css';

const shouldShowNav = (hasUserCookie) => {
  if (hasUserCookie) return true;
  if (__SERVER__) return false;
  return !['/gb', '/us', '/au'].filter((c) =>
    window.location.pathname.replace(/\/$/, '').endsWith(c),
  ).length;
};
/**
 * Header menu navigation presentational component.
 * Combines top level navigation links and user menu component.
 */
const HeaderMenuNav = ({
  categoriesVisible,
  className,
  selectedLocale,
  userSelectedLocale,
  expanded,
  hasUserCookie,
  onCategoriesClick,
  onCategoriesMouseEnterOrLeave,
  onMyEventsClick,
  onCloseClick,
  onLinkClick,
  onSearchLinkClick,
  profile,
  narrow,
  location,
  inOrganiserView,
  inLDTView,
  organiserId,
  ...domProps
}) => {
  const virtualEventsOrganizerHref = 'https://virtualevents.letsdothis.com/';
  const { data } = useProfile() ?? {};
  const { isSuperUser } = usePermissions();
  const organiserIds = data?.profile?.permissions?.organiserPermissions ?? [];
  const hasOrganisations = !!organiserIds.length;

  let organiserText = selectedLocale === 'US' ? 'Organizers' : 'Organisers';
  let organiserLink = virtualEventsOrganizerHref;

  if (hasOrganisations) {
    organiserText = 'Organiser Dashboard';
    organiserLink = getUrl.organiserDashboard(organiserIds[0]?.id);
  } else if (isSuperUser()) {
    organiserText = 'LDT Dashboard';
    organiserLink = getUrl.ldtDashboardLatestEntries();
  }

  const UserViewMenu = (
    <>
      <li className={hideAboveHeaderStackedListBreakpoint}>
        {/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
        <a
          className={searchLink}
          onClick={() => {
            onSearchLinkClick();
            onLinkClick();
          }}
        >
          Search
        </a>
      </li>
      <li className={hideAboveHeaderStackedListBreakpoint}>
        <span className={lineBreak} />
        <Link className={link} onClick={onLinkClick} to="/">
          Home
        </Link>
      </li>
      <li className={search}>
        {shouldShowNav(hasUserCookie) && <GlobalSearch />}
      </li>
      <li
        className={itemCategories}
        onMouseEnter={onCategoriesMouseEnterOrLeave}
        onMouseLeave={onCategoriesMouseEnterOrLeave}
      >
        <Link
          className={[allSportsLink, link].join(' ')}
          onClick={onCategoriesClick}
        >
          Sports
          <ArrowRightLarger className={arrow} />
        </Link>
        <CategoriesNav
          onClickBack={onCategoriesClick}
          onLinkClick={onLinkClick}
          visible={categoriesVisible}
          selectedLocale={selectedLocale}
          hasUserCookie={hasUserCookie}
          narrow={narrow}
        />
      </li>

      <li className={[itemOrganiser, narrow ? hideBelowLG : ''].join(' ')}>
        <span className={lineBreak} />
        <Link className={link} href={organiserLink}>
          {organiserText}
        </Link>
      </li>

      <li>
        <Link
          target=""
          className={link}
          onClick={onLinkClick}
          to={getUrl.communityPage()}
        >
          Community
        </Link>
      </li>
      <li>
        <Link
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={link}
          onClick={onLinkClick}
          to={getUrl.helpCenter()}
        >
          Help
        </Link>
      </li>

      {hasUserCookie && (
        <li className={hideAboveHeaderStackedListBreakpoint}>
          <Link
            className={link}
            onClick={onLinkClick}
            to={getUrl.privateProfile()}
          >
            Profile
          </Link>
        </li>
      )}

      {hasUserCookie && (
        <li className={hideAboveHeaderStackedListBreakpoint}>
          <Link
            className={link}
            onClick={onLinkClick}
            to={getUrl.privateProfileSettings()}
          >
            Settings
          </Link>
        </li>
      )}

      {hasUserCookie && (
        <li className={hideAboveHeaderStackedListBreakpoint}>
          <Link className={link} onClick={onLinkClick} to={getUrl.myEvents()}>
            Bookings
          </Link>
        </li>
      )}

      {hasUserCookie && (
        <li className={hideAboveHeaderStackedListBreakpoint}>
          <Link className={link} onClick={onLinkClick} to={getUrl.groupPage()}>
            Groups
          </Link>
        </li>
      )}
      {hasUserCookie && (
        <li className={hideAboveHeaderStackedListBreakpoint}>
          <Link
            className={link}
            onClick={onLinkClick}
            to={getUrl.savedEvents()}
          >
            Saved Events
          </Link>
        </li>
      )}
    </>
  );
  // TODO Add back settings tab when page complete: #4303
  const OrganiserViewMenu = (
    <>
      <li>
        <Link
          target=""
          className={link}
          onClick={onLinkClick}
          to={getUrl.organiserDashboard(organiserId)}
        >
          Upcoming Events
        </Link>
      </li>
      <li>
        <Link
          target=""
          className={link}
          onClick={onLinkClick}
          to={getUrl.organiserDashboardLatestEntries(organiserId)}
        >
          Search Bookings
        </Link>
      </li>
      {/* <li>
        <Link
          target=""
          className={link}
          onClick={onLinkClick}
          to={getUrl.organiserSettings(organiserId)}
        >
          Organiser Settings
        </Link>
      </li> */}
    </>
  );
  const LDTViewMenu = <div />;
  const ActionMenu =
    (inOrganiserView && OrganiserViewMenu) ||
    (inLDTView && LDTViewMenu) ||
    UserViewMenu;

  return (
    <nav
      className={`${nav} ${className}`}
      {...domProps}
      aria-expanded={expanded}
    >
      <ul className={[list, narrow ? narrowStyle : ''].join(' ')}>
        {ActionMenu}
        <li className={`${itemUser} ${hideAboveHeaderStackedListBreakpoint}`}>
          <span className={lineBreak} />
          {!hasUserCookie && (
            <Link
              className={link}
              onClick={onLinkClick}
              to={getUrl.loginPage({
                hash: { source: 'primary_navbar' },
                query: { returnUrl: getRelativeUrl(location) },
              })}
            >
              Sign Up or Log In
            </Link>
          )}
          {hasUserCookie && (
            <Link className={link} onClick={onLinkClick} to="/logout">
              Log Out
            </Link>
          )}
        </li>
      </ul>

      <UserMenu
        className={user}
        selectedLocale={selectedLocale}
        userSelectedLocale={userSelectedLocale}
        onLinkClick={onLinkClick}
        narrow={narrow}
      />

      {expanded && (
        <button type="button" className={close} onClick={onCloseClick}>
          <Icon icon={Icons.CROSS.WHITE} />
          <span className={srOnly}>Close</span>
        </button>
      )}
    </nav>
  );
};

HeaderMenuNav.propTypes = {
  categoriesVisible: PropTypes.bool,
  className: PropTypes.string,
  expanded: PropTypes.bool,
  hasUserCookie: PropTypes.bool,
  onCategoriesClick: PropTypes.func,
  onMyEventsClick: PropTypes.func,
  onCloseClick: PropTypes.func,
  onCategoriesMouseEnterOrLeave: PropTypes.func,
  onLinkClick: PropTypes.func,
  onSearchLinkClick: PropTypes.func,
  profile: PropTypes.shape(),
  selectedLocale: PropTypes.string,
  userSelectedLocale: PropTypes.string,
  narrow: PropTypes.bool,
  location: PropTypes.shape(),
  inOrganiserView: PropTypes.bool,
  inLDTView: PropTypes.bool,
  organiserId: PropTypes.string,
};

HeaderMenuNav.defaultProps = {
  categoriesVisible: false,
  className: '',
  expanded: true,
  hasUserCookie: false,
  onCategoriesClick: () => {},
  onCategoriesMouseEnterOrLeave: () => {},
  onMyEventsClick: () => {},
  onCloseClick: () => {},
  onLinkClick: () => {},
  onSearchLinkClick: () => {},
  profile: null,
  selectedLocale: undefined,
  userSelectedLocale: undefined,
  narrow: false,
  location: {},
  inOrganiserView: false,
  inLDTView: false,
  organiserId: '',
};

export default withRouter(HeaderMenuNav);
